import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Lock } from 'lucide-react';
import { useUserTrophyProgress, useUserLevel, useToggleTrophyDisplay } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import { TrophyWithProgress } from '@/types';
import TrophyCard from '@/components/progression/TrophyCard';
import LevelProgress from '@/components/progression/LevelProgress';

const CATEGORIES = ['All', 'Trading', 'Collection', 'Completion', 'Marketplace', 'Deck Building', 'Community'];

export default function TrophyCasePage() {
  const { id } = useParams<{ id?: string }>();
  const currentUser = useAuthStore((s) => s.user);
  const userId = id ?? currentUser?.id ?? '';
  const isOwn = !id || id === currentUser?.id;

  const [activeCategory, setActiveCategory] = useState('All');
  const [showLocked, setShowLocked] = useState(true);
  const [selected, setSelected] = useState<TrophyWithProgress | null>(null);

  const { data: trophies = [], isLoading } = useUserTrophyProgress(userId);
  const { data: level } = useUserLevel(userId);
  const { mutate: toggleDisplay } = useToggleTrophyDisplay(userId);

  const isMinor = isOwn && !!currentUser?.is_minor;

  const filtered = trophies.filter(t => {
    if (!showLocked && !t.earned) return false;
    if (activeCategory !== 'All' && t.category !== activeCategory) return false;
    return true;
  });

  const earnedCount  = trophies.filter(t => t.earned).length;
  const totalCount   = trophies.length;
  const byCategory   = CATEGORIES.slice(1).map(cat => ({
    name: cat,
    earned: trophies.filter(t => t.category === cat && t.earned).length,
    total:  trophies.filter(t => t.category === cat).length,
  }));

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <div className="container-custom py-8 max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={isOwn ? '/profile' : `/profile/${userId}`}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 mb-3"
          >
            <ArrowLeft className="h-4 w-4" /> Back to profile
          </Link>

          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-zinc-100">Trophy Case</h1>
              <p className="text-gray-500 dark:text-zinc-500 mt-1">
                {earnedCount} / {totalCount} trophies earned
              </p>
            </div>
            {level && <LevelProgress level={level} compact className="w-60" />}
          </div>
        </div>

        {/* Category summary pills */}
        <div className="flex gap-2 flex-wrap mb-5">
          {CATEGORIES.map(cat => {
            const stats = cat === 'All'
              ? { earned: earnedCount, total: totalCount }
              : byCategory.find(b => b.name === cat)!;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-brand-600 text-white'
                    : 'bg-white dark:bg-zinc-900 text-gray-600 dark:text-zinc-400 border border-gray-200 dark:border-zinc-800 hover:border-brand-300'
                }`}
              >
                {cat}
                <span className="ml-1.5 text-xs opacity-70">{stats?.earned ?? 0}/{stats?.total ?? 0}</span>
              </button>
            );
          })}

          <label className="ml-auto flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500 cursor-pointer">
            <input
              type="checkbox"
              checked={showLocked}
              onChange={e => setShowLocked(e.target.checked)}
              className="accent-brand-600 h-4 w-4"
            />
            Show locked
          </label>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[...Array(8)].map((_, i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-dashed border-gray-200 dark:border-zinc-800 p-12 text-center">
            <Trophy className="h-12 w-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-zinc-500">No trophies to show in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map(trophy => (
              <TrophyCard
                key={trophy.id}
                trophy={trophy}
                ageRestricted={isMinor && trophy.category === 'Marketplace'}
                onClick={() => setSelected(selected?.id === trophy.id ? null : trophy)}
              />
            ))}
          </div>
        )}

        {/* Detail panel */}
        {selected && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-md mx-auto px-4">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-zinc-800 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-gray-900 dark:text-zinc-100">{selected.name}</p>
                  <p className="text-sm text-gray-500 dark:text-zinc-500">{selected.description}</p>
                  <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1">
                    Goal: {selected.requirement_value.toLocaleString()} {selected.requirement_type.replace('_', ' ')}
                  </p>
                </div>
                {isOwn && selected.earned && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500 cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      defaultChecked
                      onChange={e => toggleDisplay({ trophyId: selected.id, isDisplayed: e.target.checked })}
                      className="accent-brand-600"
                    />
                    Show on profile
                  </label>
                )}
              </div>
              <button onClick={() => setSelected(null)} className="mt-3 text-xs text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400">
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
